import { Pagination } from './generic';

export interface FundListItem {
  id: string;
  name: string;
  account_id: string;
}

export type FundListStatusFilter = 'active' | 'inactive';

export interface FinanceFundsListParams {
  page: number;
  page_size: number;
  search?: string;
  order_by?: string;
  order?: string;
  local_chapter_id?: string;
  spd_chapter_id?: string;
  status?: FundListStatusFilter;
}

export interface FinanceFundsListResponse {
  data: FundListItem[];
  can_create?: boolean;
  pagination: Pagination;
}

export interface CreateFundRequest {
  name: string;
  account_id: string;
  short_name: string;
  can_donate: 0 | 1;
  established_by: string;
  established_date: string;
}

export interface CreateFundResponse {
  id: string;
}

export interface FundLabelOption {
  id: string;
  name: string;
}

export interface FundTypeOption {
  id: string;
  name: string;
  category_id: string;
}

export const FUND_CATEGORY_TYPE_ALLOCATION_ID =
  '435685f4d44b49a48bccc6c161b84aed';

export const FUND_TYPE_OTHER_TRANSFERS_ID = '8d674fa51e1c499d88ad6769b9ea926a';
export const FUND_TYPE_OTHER_ID = '456be14510a24182ac5f42c680345b7d';

export function fundTypeRequiresCustomName(typeId: string): boolean {
  return (
    typeId === FUND_TYPE_OTHER_TRANSFERS_ID || typeId === FUND_TYPE_OTHER_ID
  );
}

export function fundTypeAllowsMultipleRows(typeId: string): boolean {
  return typeId === FUND_TYPE_OTHER_TRANSFERS_ID;
}

export const FUND_OPTION_A = 'option_a';
export const FUND_OPTION_B = 'option_b';
export const FUND_OPTION_C = 'option_c';
export const FUND_OPTION_CUSTOM = 'custom';

export type FundOptionCode =
  | typeof FUND_OPTION_A
  | typeof FUND_OPTION_B
  | typeof FUND_OPTION_C
  | typeof FUND_OPTION_CUSTOM;

export const FUND_OPTION_LABELS: Record<FundOptionCode, string> = {
  option_a: 'Option A',
  option_b: 'Option B',
  option_c: 'Option C',
  custom: 'Custom',
};

export function fundOptionLabel(option: string): string {
  return FUND_OPTION_LABELS[option as FundOptionCode] || option;
}

export interface FundOptionDTO {
  option: string;
  description?: string;
  start_date: string;
  yearly_allocation?: number | null;
  minimum_fund_balance?: number | null;
  custom_rule_configured: boolean;
}

export interface PatchFundOptionInput {
  option: string;
  description?: string;
  yearly_allocation?: number | null;
  minimum_fund_balance?: number | null;
}

const FUND_SUBTYPES_REQUIRING_NAMED_LIMIT = new Set([
  '416fcf6e95a047f28f26ea642a728019',
  '513c589d396e46a5860fa838b301aab5',
  '5ee6ee48c3ed4b3287c3c3689cadba79',
  '6817ce08fa1c4e5aa254bd6dffba6aa9',
  '8a9ba9f5806945a7a3f8e76e68e8d209',
  '9ae274f4faa54ed4a2d0700556f6365e',
  'aa1fc548073745edb8fd2ea8c3920fef',
  'b58961145903450ab3750fb0757d5c66',
  'c2fc91984581495683bcd7c70f0d151a',
  'e5916f27f795410f9fcfa3bdf7af80d4',
  'f7c9dcece5464a5789af503fdd2bd3c3',
]);

export function fundSubtypeRequiresNamedLimit(subtypeId: string): boolean {
  return FUND_SUBTYPES_REQUIRING_NAMED_LIMIT.has(subtypeId);
}

export const NAMED_SCHOLARSHIP_LIMIT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

export interface FundAssignedTypeDTO {
  id?: string;
  type_id: string;
  type_name?: string;
  subtype_id?: string;
  subtype_name?: string;
  percentage: number;
  other_transfer_name?: string;
  amount?: number | null;
  named_limit?: number | null;
}

export interface FundAssignedTypeInput {
  type_id: string;
  subtype_id?: string | null;
  percentage?: number;
  other_transfer_name?: string;
  amount?: number | null;
  named_limit?: number | null;
}

export interface FundNamedAmountDTO {
  id: string;
  amount: number;
  type_id: string;
  type_name?: string;
  start_date: string;
  end_date?: string | null;
}

export interface CreateFundNamedAmountRequest {
  amount: number;
  type_id: string;
  start_date: string;
  end_date?: string | null;
}

export interface PatchFundNamedAmountRequest {
  amount?: number;
  type_id?: string;
  start_date?: string;
  end_date?: string | null;
}

export interface FundSubtypeOption {
  id: string;
  name: string;
  type_id: string;
}

export interface FundAttachmentType {
  id: string;
  name: string;
  description?: string;
  sort_order: number;
}

export interface FundAttachmentItem {
  id: string;
  file_name: string;
  description?: string;
  content_type?: string;
  file_size_bytes?: number;
  uploaded_by_name?: string;
  uploaded_at?: string;
  attachment_type_id: string;
}

export interface FundAttachmentGroup {
  type_id: string;
  type_name: string;
  type_sort_order: number;
  attachments: FundAttachmentItem[];
}

export interface FundAttachmentsGroupedResponse {
  groups: FundAttachmentGroup[];
}

export interface PatchFundAttachmentRequest {
  description?: string;
  attachment_type_id?: string;
}

export interface FundNoteDTO {
  id: string;
  note: string;
  created_at?: string;
  created_by_id: string;
  created_by_name?: string;
}

export interface FundPositionDTO {
  id: string;
  user_id: string;
  name: string;
  position: string;
  position_id: string;
  start_date?: string;
  end_date?: string | null;
}

export interface ChairRoleDTO {
  id: string;
  name: string;
}

export interface FundRoleSlotDTO {
  id: string;
  group_role_id: string;
  group_role_name: string;
  ref_group_role_id: string;
  ref_group_role_name: string;
  local_chapter_id?: string;
  local_chapter_name?: string;
  spd_chapter_id?: string;
  spd_chapter_name?: string;
  display_label: string;
  start_date?: string;
  end_date?: string | null;
  is_active: boolean;
}

export interface FundDetailResponse {
  id: string;
  name: string;
  short_name?: string;
  account_id?: string;
  description?: string;
  can_donate: number;
  sponser_chapter_code?: string;
  type_id?: string;
  type_name?: string;
  category_id?: string;
  category_name?: string;
  subtype_id?: string;
  subtype_name?: string;
  established_by?: string;
  established_date?: string | null;
  state_id?: string;
  status_id?: string;
  status_name?: string;
  local_chapter_id?: string;
  local_chapter_name?: string;
  spd_chapter_id?: string;
  spd_chapter_name?: string;
  opt_out: number;
  selection_committee?: string;
  transfer_letter?: string;
  statement_paragraph?: string;
  activity_letter?: string;
  fund_use?: string;
  principal_amount?: number | null;
  current_fund_option?: FundOptionDTO | null;
  notes: FundNoteDTO[];
  positions: FundPositionDTO[];
  group_id?: string | null;
  chair_roles: ChairRoleDTO[];
  chair_role_slots: FundRoleSlotDTO[];
  email_group_id?: string | null;
  email_members: FundPositionDTO[];
  email_roles: ChairRoleDTO[];
  email_role_slots: FundRoleSlotDTO[];
  ref_role_options: FundLabelOption[];
  fund_type_options: FundTypeOption[];
  fund_category_options: FundLabelOption[];
  fund_subtype_options: FundSubtypeOption[];
  fund_status_options: FundLabelOption[];
  uses_type_allocations?: boolean;
  assigned_types?: FundAssignedTypeDTO[];
  is_primary?: number;
  uses_named_amounts?: boolean;
  named_amounts?: FundNamedAmountDTO[];
  can_manage: boolean;
  can_manage_attachments: boolean;
  can_manage_statement_data?: boolean;
}

export interface PatchFundRequest {
  name?: string;
  short_name?: string;
  account_id?: string;
  description?: string;
  can_donate?: number;
  sponser_chapter_code?: string;
  type_id?: string | null;
  /** Set to empty string to clear; omit to leave unchanged. */
  category_id?: string;
  subtype_id?: string | null;
  status_id?: string | null;
  /** Set to empty string to clear; omit to leave unchanged. */
  local_chapter_id?: string;
  /** Set to empty string to clear; omit to leave unchanged. */
  spd_chapter_id?: string;
  established_by?: string;
  established_date?: string | null;
  opt_out?: number;
  selection_committee?: string;
  transfer_letter?: string;
  statement_paragraph?: string;
  activity_letter?: string;
  fund_use?: string;
  principal_amount?: number | null;
  /** YYYY-MM-DD; stamps fund history for tracked field changes. Default today on server if omitted. */
  as_of_date?: string;
  fund_option?: PatchFundOptionInput;
  assigned_types?: FundAssignedTypeInput[] | null;
}

export interface CreateFundNoteRequest {
  note: string;
}

export interface PatchFundNoteRequest {
  note: string;
}

export interface CreateFundPositionRequest {
  user_id: string;
  role_id: string;
  start_date: string;
  end_date?: string | null;
  apply_to_other_funds?: boolean;
}

export interface ReplaceFundPositionRequest {
  user_id: string;
  start_date: string;
  end_date?: string | null;
  apply_to_other_funds?: boolean;
}

export interface FundChairRoleHistoryMember {
  user_id: string;
  name: string;
  start_date: string;
  end_date?: string | null;
}

export interface FundChairRoleHistoryResponse {
  future: FundChairRoleHistoryMember[];
  past: FundChairRoleHistoryMember[];
}

export interface FundChairOtherFundDTO {
  fund_id: string;
  fund_name: string;
  role_id: string;
  role_name: string;
  group_member_id: string;
}

export interface FundChairOtherFundsResponse {
  other_funds: FundChairOtherFundDTO[];
}

export interface PatchFundPositionRequest {
  user_id?: string;
  role_id?: string;
  start_date?: string;
  end_date?: string | null;
}

export interface CreateFundRoleSlotRequest {
  group_role_id: string;
  ref_group_role_id: string;
  local_chapter_id?: string | null;
  spd_chapter_id?: string | null;
  start_date: string;
  end_date?: string | null;
}

export interface PatchFundRoleSlotRequest {
  group_role_id?: string;
  ref_group_role_id?: string;
  local_chapter_id?: string | null;
  spd_chapter_id?: string | null;
  start_date?: string;
  end_date?: string | null;
}

export interface CreateFundEmailContactRequest {
  user_id: string;
  start_date: string;
  end_date?: string | null;
}

export interface PatchFundEmailContactRequest {
  user_id?: string;
  start_date?: string;
  end_date?: string | null;
}

export type FundHistoryKind = 'field_change' | 'fund_option' | 'named_amount';

export interface FundHistoryItem {
  kind: FundHistoryKind;
  sort_date: string;
  field_name?: string;
  field_label?: string;
  old_value?: string;
  new_value?: string;
  old_label?: string;
  new_label?: string;
  as_of_date?: string;
  created_at?: string;
  created_by_id?: string;
  created_by_name?: string;
  start_date?: string;
  end_date?: string | null;
  summary?: string;
}

export interface FundHistoryResponse {
  items: FundHistoryItem[];
}

export interface FundStatementDatesResponse {
  dates: string[];
}

export interface FundStatementFieldDTO {
  field_name: string;
  label: string;
  base_value: number | null;
  effective_value: number | null;
  is_overridden: boolean;
}

export interface FundStatementOverrideDTO {
  id: string;
  field_name: string;
  field_label: string;
  old_value?: string;
  new_value?: string;
  reason?: string;
  created_by_id?: string;
  created_by_name?: string;
  created_at?: string;
}

export interface FundStatementCalculation {
  computed_ending: number;
  balance_ok: boolean;
  balance_diff: number;
  option_code?: string;
  option_label?: string;
  all_contributions?: number | null;
  prior_principal_retained?: number | null;
  principal_retained?: number | null;
  stored_principal_retained?: number | null;
  missing_prior_principal?: boolean;
  principal_retained_mismatch?: boolean;
  principal_retained_needs_save?: boolean;
  principal_retained_is_seed?: boolean;
  surplus?: number | null;
  principal_amount?: number | null;
  yearly_allocation?: number | null;
  can_award_predetermined?: boolean;
  available_income?: number | null;
  available_income_label?: string;
  custom_not_implemented?: boolean;
  custom_message?: string;
}

export interface FundStatementDataResponse {
  mip_data_id: string;
  as_of_date: string;
  previous_balance_date?: string | null;
  ending_balance_date?: string | null;
  fields: FundStatementFieldDTO[];
  overrides: FundStatementOverrideDTO[];
  calculation?: FundStatementCalculation | null;
  fund_name?: string;
  account_id?: string;
  type_id?: string;
  type_name?: string;
  category_id?: string;
  category_name?: string;
  description?: string;
}

export interface PostFundStatementOverrideRequest {
  as_of_date: string;
  field_name: string;
  new_value?: number | null;
  reason?: string;
  clear?: boolean;
}
